// src/pages/Payments.jsx
import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../services/api";

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selectedDevice, setSelectedDevice] = useState(null);

  const fetchPayments = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/payments");
      setPayments(res.data || []);
    } catch (err) {
      setError("Could not load payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, []);

  const filtered = payments.filter(p =>
    !query ||
    p.customer_name?.toLowerCase().includes(query.toLowerCase()) ||
    p.phone?.includes(query) ||
    p.order_id?.includes(query)
  );

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      customer_name: p.customer_name,
      phone: p.phone,
      email: p.email,
      due_date: p.due_date || "",
    });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/payments/${id}`, editForm);
      setEditingId(null);
      fetchPayments();
    } catch {
      alert("Update failed");
    }
  };

  const downloadCSV = () => {
    const headers = [
      "Date","Customer","Agent","Phone","Email","Order ID",
      "Transaction ID","Amount","Status","Device Info"
    ];

    const rows = filtered.map(p => [
      p.created_at ? new Date(p.created_at).toLocaleString("en-IN") : "",
      p.customer_name,
      p.agent_name || "",
      p.phone,
      p.email,
      p.order_id,
      p.transaction_id,
      Number(p.amount || 0).toFixed(2),
      p.payment_status,
      JSON.stringify(p.device_info || {})
    ]);

    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${(c||"").toString().replace(/"/g,'""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = `core60_sales_${Date.now()}.csv`;
    link.click();
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-indigo-600">All Sales</h1>

          <button
            onClick={downloadCSV}
            className="bg-green-600 text-white px-4 py-2 rounded text-sm"
          >
            Export CSV
          </button>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded">{error}</div>}

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name / phone / order"
          className="mb-4 p-2 border rounded w-72 bg-white text-black"
        />

        <div className="bg-white rounded shadow overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Agent</th> {/* ✅ ADDED */}
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Order</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Transaction ID</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Due Date</th>
                <th className="px-3 py-2">Device</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-t">

                  <td className="px-3 py-2">
                    {editingId === p.id ? (
                      <input
                        value={editForm.customer_name}
                        onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                        className="border p-1 rounded w-full"
                      />
                    ) : p.customer_name}
                  </td>

                  {/* ✅ AGENT COLUMN */}
                  <td className="px-3 py-2">
                    {p.agent_name || "-"}
                  </td>

                  <td className="px-3 py-2">
                    {editingId === p.id ? (
                      <input
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="border p-1 rounded w-full"
                      />
                    ) : p.phone}
                  </td>

                  <td className="px-3 py-2">
                    {editingId === p.id ? (
                      <input
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="border p-1 rounded w-full"
                      />
                    ) : p.email}
                  </td>

                  <td className="px-3 py-2">{p.order_id}</td>

                  <td className="px-3 py-2">
                    ₹ {Number(p.amount || 0).toFixed(2)}
                  </td>

                  <td className="px-3 py-2">
                    {p.transaction_id || "-"}
                  </td>

                  <td className="px-3 py-2">
                    {p.created_at ? new Date(p.created_at).toLocaleString("en-IN") : "-"}
                  </td>

                  <td className="px-3 py-2">
                    {editingId === p.id ? (
                      <input
                        type="date"
                        value={editForm.due_date}
                        onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                        className="border p-1 rounded"
                      />
                    ) : (p.due_date || "-")}
                  </td>

                  <td className="px-3 py-2">
                    <button
                      onClick={() => {
                        try {
                          const parsed =
                            typeof p.device_info === "string"
                              ? JSON.parse(p.device_info)
                              : p.device_info;
                          setSelectedDevice(parsed);
                        } catch {
                          setSelectedDevice({ error: "Invalid device data" });
                        }
                      }}
                      className="bg-gray-700 text-white px-2 py-1 rounded text-xs"
                    >
                      View
                    </button>
                  </td>

                  <td className="px-3 py-2">
                    {editingId === p.id ? (
                      <button
                        onClick={() => saveEdit(p.id)}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm"
                      >
                        Save
                      </button>
                    ) : (
                      <button
                        onClick={() => startEdit(p)}
                        className="bg-indigo-600 text-white px-3 py-1 rounded text-sm"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="p-4 text-center text-gray-500">
                    No sales found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {selectedDevice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded w-[600px] max-h-[80vh] overflow-auto">
              <h2 className="text-lg font-bold mb-2">Device Info</h2>

              <pre className="text-xs whitespace-pre-wrap">
                {JSON.stringify(selectedDevice, null, 2)}
              </pre>

              <button
                onClick={() => setSelectedDevice(null)}
                className="mt-4 bg-red-600 text-white px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {loading && <div className="mt-4 text-center text-gray-500">Loading…</div>}
      </div>
    </Layout>
  );
}